import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import { registerView, View, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import RecapViewModel from "viewModels/eshop/order/steps/recapViewModel";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import Portal from "../components/portal";
import { EDAZGooglePayButton } from "../components/googlePayButton";
import { turnstileSiteKey } from "config";
import styles from "../components/toolboxView.module.scss";
import { ChargeOrderStatus } from "models/chargeOrder";
import { isMobile } from "react-device-detect";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import LicenseViewModel from "viewModels/eshop/order/steps/licenseViewModel";

const RecapView: ViewComponent<RecapViewModel> = observer(({ vm }) => {
  const tg = (value: string) => vm.localizationService.translateGeneral(`eshop.order.${value}`);
  const iframeProps = {
    allowpaymentrequest: "true",
  };

  const formRef = useCallback(node => {
    if (node !== null) {
      vm.formElement = node;
    }
  }, []);

  useEffect(() => {
    const handlePageShow = async () => {
      const reloaded = localStorage.getItem("orderReloaded");
      const lastChargeOrder = vm.storage.getLastChargeOrder();
      if (reloaded === "true") return;
      if (vm.chargeOrderService.chargeOrder.id && vm.chargeOrderService.chargeOrder._status !== ChargeOrderStatus.Open) {
        await vm.getOrderStatus(vm.chargeOrderService.chargeOrder.id);
      }

      if (vm.isOrderComplete) {
        vm.onOrderIsCompleteCheck(vm.chargeOrderService.chargeOrder.id);
      }
      if (lastChargeOrder && !lastChargeOrder.charges[0].licensePlate) {
        vm.chargeOrderService.reset();
        vm.router.navigate(LicenseViewModel);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("popstate", handlePageShow);
    window.addEventListener("beforeunload", handlePageShow);

    return () => {
      handlePageShow();
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return (
    <>
      <div>
        <Form ref={formRef}>
          <div className="pb-7">
            <View vm={vm.paymentViewModel} />
          </div>
          <AlertWithInfoIcon variant="warning">
            <span>{vm.translate("alertRecapNow")}</span>
          </AlertWithInfoIcon>
          <View vm={vm.orderRecapitulationViewModel} />
          <View vm={vm.termsAgreementViewModel} />
        </Form>
      </div>
      {vm.isTransferPayment && vm.isChecked && <View vm={vm.turnstileViewModel} />}

      <Row className="justify-content-center pt-2 pt-lg-2 pt-xl-2">
        {/* Back button visible only in mobile view */}
        <Col lg={10} xl={4} className="p-2 w-auto d-md-none">
          <Button
            type="button"
            variant="light"
            className="btn-block px-lg-4 w-auto w-lg-100"
            onClick={vm.onBack}
            iconLeft={IconType.ArrowLeft}></Button>
        </Col>
        {/* Back button visible only in tablet/desktop view */}
        <Col md={5} xl={4} className="d-md-block p-2 d-none">
          <Button type="button" variant="light" className="btn-block px-lg-4" onClick={vm.onBack} iconLeft={IconType.ArrowLeft}>
            {tg("back")}
          </Button>
        </Col>

        <Col md={5} xl={4} className="flex-grow-1 p-2 w-auto">
          {vm.isGooglePayPaymentMethodSelected && !!vm.isDisabled ? (
            <>
              <div className="d-block mx-auto mt-1 mb-2 mb-lg-0 text-center mw-100">
                <EDAZGooglePayButton
                  totalPrice={vm.orderRecapitulationViewModel.price}
                  locale={vm.locale}
                  googlePayMetadata={vm.googlePayMetadata!}
                  loading={!vm.isGooglePayMetadataReady}
                  createOrder={vm.payOrder}
                  validateOrder={vm.validateOrder}
                  errorMessage={vm.localizationService.translateGeneral("errors.generic")}
                />
              </div>
              {vm.googlePayPaymentFingerprint && (
                <iframe
                  {...iframeProps}
                  className={styles.fingerprintIframe}
                  onLoad={vm.onGooglePayPaymentFingerprintLoaded}
                  src={vm.googlePayPaymentFingerprint}></iframe>
              )}
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              className="btn-block px-4"
              onClick={() => vm.onContinue()}
              disabled={
                (!vm.turnstileViewModel.turnstileToken || vm.turnstileViewModel.isDisabledByTurnsitel) &&
                vm.isTransferPayment &&
                vm.isChecked
              }
              iconRight={IconType.ArrowRight}>
              {tg("payment")}
            </Button>
          )}
        </Col>
      </Row>
    </>
  );
});

registerView(RecapView, RecapViewModel);
registerView(RecapView, RecapViewModel);
